// Blog utility functions
import config from './config';

// Get the API base URL without /api suffix for image URLs
const getImageBaseUrl = () => {
  // In development, use empty string so images go through the proxy too
  if (process.env.NODE_ENV === 'development') {
    return '';
  }
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3002';
  return apiUrl.replace(/\/api$/, '');
};

/**
 * Process blog content to ensure all image URLs are correct
 * Converts relative URLs and localhost URLs to proper API URLs
 */
export const processBlogContent = (content) => {
  if (!content) return '';
  
  const imageBaseUrl = getImageBaseUrl();
  
  // Replace image URLs in markdown format: ![alt](url)
  let processedContent = content.replace(
    /!\[([^\]]*)\]\(([^)]+)\)/g,
    (match, alt, url) => {
      const processedUrl = processImageUrl(url, imageBaseUrl);
      return `![${alt}](${processedUrl})`;
    }
  );
  
  // Replace image URLs in HTML format: <img src="url">
  processedContent = processedContent.replace(
    /<img([^>]*?)src=["']([^"']+)["']([^>]*?)>/g,
    (match, before, url, after) => {
      const processedUrl = processImageUrl(url, imageBaseUrl);
      return `<img${before}src="${processedUrl}"${after}>`;
    }
  );
  
  return processedContent;
};

/**
 * Process a single image URL to ensure it's accessible
 */
export const processImageUrl = (url, imageBaseUrl = null) => {
  if (!url) return '';
  
  const baseUrl = imageBaseUrl || getImageBaseUrl();
  
  // If URL is already absolute with correct domain, check for localhost issues
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Replace localhost:3001 or localhost:3002 with the correct API URL
    if (url.includes('localhost:3001') || url.includes('localhost:3002')) {
      return url.replace(/http:\/\/localhost:(3001|3002)/, baseUrl);
    }
    return url;
  }
  
  // If URL starts with /uploads, prepend the API base URL
  if (url.startsWith('/uploads')) {
    return `${baseUrl}${url}`;
  }
  
  // If URL starts with /api/uploads, replace /api with base URL
  if (url.startsWith('/api/uploads')) {
    return `${baseUrl}${url.replace('/api', '')}`;
  }
  
  // If URL is relative (doesn't start with /), prepend /uploads and base URL
  if (!url.startsWith('/')) {
    return `${baseUrl}/uploads/${url}`;
  }
  
  // Default: prepend base URL
  return `${baseUrl}${url}`;
};

/**
 * Process blog post object to ensure all image URLs are correct
 */
export const processBlogPost = (post) => {
  if (!post) return null;
  
  const imageBaseUrl = getImageBaseUrl();
  
  return {
    ...post,
    // Process content
    content: processBlogContent(post.content),
    // Process featured image
    featuredImage: post.featuredImage ? processImageUrl(post.featuredImage, imageBaseUrl) : post.featuredImage,
    // Process image field (alternative name)
    image: post.image ? processImageUrl(post.image, imageBaseUrl) : post.image,
    // Process SEO images
    seo: post.seo ? {
      ...post.seo,
      openGraph: post.seo.openGraph ? {
        ...post.seo.openGraph,
        image: post.seo.openGraph.image ? processImageUrl(post.seo.openGraph.image, imageBaseUrl) : post.seo.openGraph.image
      } : post.seo.openGraph,
      twitterCard: post.seo.twitterCard ? {
        ...post.seo.twitterCard,
        image: post.seo.twitterCard.image ? processImageUrl(post.seo.twitterCard.image, imageBaseUrl) : post.seo.twitterCard.image
      } : post.seo.twitterCard
    } : post.seo
  };
};

/**
 * Process array of blog posts
 */
export const processBlogPosts = (posts) => {
  if (!Array.isArray(posts)) return [];
  return posts.map(post => processBlogPost(post));
};

/**
 * Convert markdown to HTML (basic implementation)
 * For production, consider using a library like marked or react-markdown
 */
export const markdownToHtml = (markdown) => {
  if (!markdown) return '';
  
  let html = markdown;
  
  // Headers
  html = html.replace(/^### (.*$)/gim, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gim, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gim, '<h1>$1</h1>');
  
  // Bold
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Italic
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  
  // Images (already processed URLs)
  html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img src="$2" alt="$1" class="blog-image" />');
  
  // Line breaks
  html = html.replace(/\n\n/g, '</p><p>');
  html = html.replace(/\n/g, '<br />');
  
  // Wrap in paragraphs
  html = '<p>' + html + '</p>';
  
  // Lists
  html = html.replace(/<p>- (.*?)<\/p>/g, '<ul><li>$1</li></ul>');
  html = html.replace(/<\/ul><ul>/g, '');
  
  return html;
};

export default {
  processBlogContent,
  processImageUrl,
  processBlogPost,
  processBlogPosts,
  markdownToHtml
};
